import {
  EXTENSION_ID,
  EXTENSION_INFO_TIMEOUT_MS,
} from '@/constants/external/extension';
import {
  EXTENSION_MESSAGE_TYPES,
  EXTENSION_STORAGE_KEYS,
} from '@/constants/external/extension-message-types';
import {
  EXTENSION_MESSAGE_TYPE,
  IExtensionInfo,
  IExtensionUserResponse,
} from '@/models';
import { browserUtils } from '@/utils';
import { logger } from '@/utils';
import { LOGGER_FEATURE, LOGGER_ACTION } from '@/constants/logger/logger-tags';
import { STORAGE_KEYS } from '@/constants/storage/storage';
import { localStorageUtils } from '@/utils/storage/localStorage';

class ExtensionUtils {
  /**
   * Get the appropriate extension ID based on the browser
   * Note: This returns the expected extension ID for each browser,
   * but Edge users might have the Chrome extension installed instead
   */
  public getExtensionId(): string {
    if (browserUtils.isChromium()) {
      if (browserUtils.isEdge()) {
        return EXTENSION_ID.EDGE;
      }
      return EXTENSION_ID.CHROME;
    } else if (browserUtils.isSafariIOS()) {
      return EXTENSION_ID.SAFARI_IOS;
    } else if (browserUtils.isSafariChrome()) {
      return EXTENSION_ID.SAFARI_CHROMEIUM;
    }
    return EXTENSION_ID.CHROME;
  }

  /**
   * Check if the extension is installed
   */
  public async getExtensionInfo(): Promise<IExtensionInfo> {
    const emptyInfo: IExtensionInfo = {
      extensionVersion: '',
      isInstalled: null,
      installedTime: 0,
    };
    const extensionResponse = await this.sendMessageToExtension<IExtensionInfo>(
      EXTENSION_MESSAGE_TYPE.GET_INFO,
      emptyInfo,
      EXTENSION_INFO_TIMEOUT_MS
    );
    return !extensionResponse || extensionResponse?.error
      ? { ...emptyInfo, ...{ isInstalled: false } }
      : { ...emptyInfo, ...{ isInstalled: true }, ...extensionResponse };
  }

  /**
   * Request user data from extension for syncing anonymous users
   */
  public async askForExtensionUser(): Promise<IExtensionUserResponse | null> {
    const response = await this.sendMessageToExtension<IExtensionUserResponse>(
      EXTENSION_MESSAGE_TYPES.GET_USER,
      undefined,
      2000
    );
    return response?.uid ? response : null;
  }

  /**
   * Request the current user's Firebase ID token from the extension.
   * Used to sign in on the web app when the extension has a session (e.g. incognito).
   * Extension returns object with success: true - token or success: false- error message  .
   */
  public async getUserIdTokenFromExtension(): Promise<string | null> {
    const response = await this.sendMessageToExtension<{
      token?: string;
      error?: string;
    }>(EXTENSION_MESSAGE_TYPES.GET_USER_ID_TOKEN, undefined, 2000);
    if (!response?.token || response.error) return null;
    return response.token;
  }

  /**
   * Keeps the extension premium status in line with the status the web app just got from the server.
   * Asks the extension to re-check only when the status it stores differs, and skips when it cannot
   * report a boolean (not installed or an older version). Serves the extension only: callers must
   * not await it and the web app never depends on its result.
   */
  public async syncPremiumStatus(isPremium: boolean): Promise<void> {
    try {
      const stored = await this.sendMessageToExtension<{
        isPremium?: unknown;
      }>(EXTENSION_MESSAGE_TYPES.GET_CONFIG, {
        keys: [EXTENSION_STORAGE_KEYS.IS_PREMIUM],
      });
      const extensionIsPremium = stored?.isPremium;
      if (
        typeof extensionIsPremium !== 'boolean' ||
        extensionIsPremium === isPremium
      ) {
        return;
      }
      await this.sendMessageToExtension(EXTENSION_MESSAGE_TYPES.PURCHASED);
    } catch (error) {
      logger.error('Failed to sync premium status to extension', error, {
        tags: {
          feature: LOGGER_FEATURE.EXTENSION,
          action: LOGGER_ACTION.CONFIG_UPDATE,
        },
        extra: { isPremium },
      });
    }
  }

  /**
   * Send a message to the extension
   * For Edge users: Will automatically try Chrome extension ID as fallback
   */
  public async sendMessageToExtension<T>(
    messageType: string,
    payload?: any,
    ttl = 1000
  ): Promise<T | null> {
    const extensionId = this.getExtensionId();
    if (!extensionId) return null;

    // Try with the default extension ID first
    const response = await this.sendMessageToExtensionWithId<T>(
      extensionId,
      messageType,
      payload,
      ttl
    );

    // If we're on Edge and the first attempt failed, try with Chrome extension ID
    // This handles the case where users installed the Chrome extension on Edge
    if (
      !response &&
      browserUtils.isEdge() &&
      extensionId !== EXTENSION_ID.CHROME
    ) {
      logger.info('Retrying message with Chrome extension ID on Edge', {
        tags: {
          feature: LOGGER_FEATURE.EXTENSION,
          action: LOGGER_ACTION.SEND_MESSAGE,
        },
        extra: {
          messageType,
          originalId: extensionId,
          fallbackId: EXTENSION_ID.CHROME,
        },
      });

      return await this.sendMessageToExtensionWithId<T>(
        EXTENSION_ID.CHROME,
        messageType,
        payload,
        ttl
      );
    }

    return response;
  }

  /**
   * Send a message to the extension with a specific extension ID
   * This is useful when trying to communicate with Chrome extension installed on Edge
   */
  public async sendMessageToExtensionWithId<T>(
    extensionId: string,
    messageType: string,
    payload?: any,
    ttl = 1000
  ): Promise<T | null> {
    let timeout: ReturnType<typeof setTimeout>;

    const browserApi: any = browserUtils.getBrowserApi();
    if (!browserApi) {
      return null;
    }
    return new Promise(resolve => {
      if (!browserApi?.runtime?.sendMessage) {
        resolve(null);
        return;
      }
      timeout = setTimeout(() => {
        resolve(null);
      }, ttl);
      try {
        const message = { type: messageType, payload };

        // Use callback API for compatibility with older Chrome versions
        // Some browsers return undefined, some return a Promise
        const result = browserApi.runtime.sendMessage(
          extensionId,
          message,
          (response: T) => {
            // Callback-based API (works in all Chrome versions)
            clearTimeout(timeout);
            // Check for chrome.runtime.lastError to avoid unchecked error warnings
            if (browserApi.runtime.lastError) {
              resolve(null);
              return;
            }
            resolve(response as T);
          }
        );

        // If sendMessage returns a Promise (modern browsers), handle its rejection
        if (result && typeof result.then === 'function') {
          result.catch(() => {
            // Ignore - already handled by callback or timeout
          });
        }
      } catch (error: any) {
        clearTimeout(timeout);
        logger.error('Failed to send message to browser extension', error, {
          tags: {
            feature: LOGGER_FEATURE.EXTENSION,
            action: LOGGER_ACTION.SEND_MESSAGE,
          },
          extra: { extensionId },
        });
        resolve(null);
      }
    });
  }

  public saveFirstKnownInstalledTime(installedTime: number): void {
    if (installedTime === 0) return;

    const existingValue = localStorageUtils.getNumberItem(
      STORAGE_KEYS.LAST_KNOWN_INSTALLED_TIME,
      0
    );

    if (existingValue === 0) {
      localStorageUtils.setItem(
        STORAGE_KEYS.LAST_KNOWN_INSTALLED_TIME,
        installedTime
      );
    }
  }
}

export const extensionUtils = new ExtensionUtils();
